import React, { lazy } from 'react';
import { routes } from './routes';
import { AccountSetup } from '@/pages/account-setup/account-setup';

const Home = lazy(() =>
  import('../pages/home/home').then((module) => ({ default: module.Home }))
);
const Login = lazy(() =>
  import('../pages/auth/login').then((module) => ({ default: module.Login }))
);
const Dashboard = lazy(() =>
  import('../pages/dashboard/dashboard').then((module) => ({
    default: module.Dashboard,
  }))
);
const Copilot = lazy(() =>
  import('../pages/copilot/copilot').then((module) => ({
    default: module.AiCopilotPage,
  }))
);
const Payments = lazy(() =>
  import('../pages/payments/payments').then((module) => ({
    default: module.Payments,
  }))
);
const Ledger = lazy(() =>
  import('../pages/ledger/ledger').then((module) => ({
    default: module.Ledger,
  }))
);
const Campaigns = lazy(() =>
  import('../pages/campaign/campaigns').then((module) => ({
    default: module.CampaignsPage,
  }))
);
const CampaignCreate = lazy(() =>
  import('../pages/campaign/campaign').then((module) => ({
    default: module.CampaignPage,
  }))
);
const CreativeSuite = lazy(() =>
  import('../pages/creative-suite/creative-suite').then((module) => ({
    default: module.CreativeSuite,
  }))
);
const AddCreativePage = lazy(() =>
  import('../pages/creative-suite/add-creative/add-creative-page').then(
    (module) => ({ default: module.AddCreativePage })
  )
);
const EditCreativePage = lazy(() =>
  import('../pages/creative-suite/edit-creative/edit-creative-page').then(
    (module) => ({ default: module.EditCreativePage })
  )
);
const CreativeTemplateEditor = lazy(() =>
  import('../pages/creative-suite/editor/template-editor-page').then(
    (module) => ({
      default: module.CreativeTemplateEditorPage,
    })
  )
);
const AccountSettings = lazy(() =>
  import('../pages/account-settings/account-settings').then((module) => ({
    default: module.AccountSettings,
  }))
);
const Analytics = lazy(() =>
  import('../pages/analytics/analytics').then((module) => ({
    default: module.Analytics,
  }))
);
const AnalyticsCampaignDetail = lazy(() =>
  import('../pages/analytics/analytics-campaign-detail').then((module) => ({
    default: module.AnalyticsCampaignDetail,
  }))
);
const Audiences = lazy(() =>
  import('../pages/audiences/audiences').then((module) => ({
    default: module.Audiences,
  }))
);

// Admin pages
const AdminLogin = lazy(() =>
  import('../pages/admin/login').then((module) => ({
    default: module.AdminLogin,
  }))
);
const AdminDashboard = lazy(() =>
  import('../pages/admin/dashboard/dashboard').then((module) => ({
    default: module.AdminDashboard,
  }))
);
const AdminTenants = lazy(() =>
  import('../pages/admin/tenants/tenants').then((module) => ({
    default: module.AdminTenants,
  }))
);
const AdminTenantForm = lazy(() =>
  import('../pages/admin/tenants/tenant-form').then((module) => ({
    default: module.TenantFormPage,
  }))
);
const AdminTenantDetail = lazy(() =>
  import('../pages/admin/tenants/tenant-detail').then((module) => ({
    default: module.TenantDetailPage,
  }))
);
const AdminReferenceData = lazy(() =>
  import('../pages/admin/reference-data/reference-data').then((module) => ({
    default: module.AdminReferenceData,
  }))
);
const AdminBusinesses = lazy(() =>
  import('../pages/admin/businesses/businesses').then((module) => ({
    default: module.AdminBusinesses,
  }))
);
const AdminBusinessDetail = lazy(() =>
  import('../pages/admin/businesses/business-detail').then((module) => ({
    default: module.BusinessDetailPage,
  }))
);
const AdminCampaigns = lazy(() =>
  import('../pages/admin/campaigns/campaigns').then((module) => ({
    default: module.AdminCampaigns,
  }))
);
const AdminCampaignDetail = lazy(() =>
  import('../pages/admin/campaigns/campaign-detail').then((module) => ({
    default: module.CampaignDetailPage,
  }))
);
const AdminAuditLogs = lazy(() =>
  import('../pages/admin/audit-logs/audit-logs').then((module) => ({
    default: module.AdminAuditLogs,
  }))
);
const AdminPaymentsHealth = lazy(() =>
  import('../pages/admin/payments-health/payments-health').then((module) => ({
    default: module.AdminPaymentsHealth,
  }))
);

// Tenant staff pages
const TenantStaffLogin = lazy(() =>
  import('../pages/tenant-staff/login').then((module) => ({
    default: module.TenantStaffLogin,
  }))
);
const TenantStaffDashboard = lazy(() =>
  import('../pages/tenant-staff/dashboard/dashboard').then((module) => ({
    default: module.TenantStaffDashboard,
  }))
);
const TenantStaffCampaigns = lazy(() =>
  import('../pages/tenant-staff/campaigns/campaigns').then((module) => ({
    default: module.TenantStaffCampaigns,
  }))
);
const TenantStaffAdvertisers = lazy(() =>
  import('../pages/tenant-staff/advertisers/advertisers').then((module) => ({
    default: module.TenantStaffAdvertisers,
  }))
);
const TenantStaffBusinessDetail = lazy(() =>
  import('../pages/tenant-staff/advertisers/business-detail').then(
    (module) => ({ default: module.TenantStaffBusinessDetail })
  )
);
const TenantStaffUsers = lazy(() =>
  import('../pages/tenant-staff/users/users').then((module) => ({
    default: module.TenantStaffUsers,
  }))
);
const TenantStaffStaff = lazy(() =>
  import('../pages/tenant-staff/staff/staff').then((module) => ({
    default: module.TenantStaffStaff,
  }))
);
const TenantStaffConsolidatedReport = lazy(() =>
  import('../pages/tenant-staff/reports/consolidated-report').then(
    (module) => ({
      default: module.TenantStaffConsolidatedReport,
    })
  )
);

export const noAuthRoutes = [
  { path: routes.home, element: <Home /> },
  { path: routes.login, element: <Login /> },
];

export const authRoutes = [
  { path: routes.dashboard, element: <Dashboard /> },
  { path: routes.analytics, element: <Analytics /> },
  {
    path: routes.analyticsCampaignDetail,
    element: <AnalyticsCampaignDetail />,
  },
  { path: routes.audiences, element: <Audiences /> },
  { path: routes.payments, element: <Payments /> },
  { path: routes.ledger, element: <Ledger /> },
  { path: routes.campaign, element: <Campaigns /> },
  { path: routes.campaignCreate, element: <CampaignCreate /> },
  { path: routes.accountSetup, element: <AccountSetup /> },
  { path: routes.aiCopilot, element: <Copilot /> },
  { path: routes.accountSettings, element: <AccountSettings /> },
  { path: routes.creativeSuite, element: <CreativeSuite /> },
  { path: routes.creativeSuiteAdd, element: <AddCreativePage /> },
  { path: routes.creativeSuiteEdit, element: <EditCreativePage /> },
  {
    path: routes.creativeSuiteCopy,
    element: <EditCreativePage isCopy />,
  },
  { path: routes.creativeSuiteEditor, element: <CreativeTemplateEditor /> },
];

// Admin routes (no auth required for login, auth required for dashboard)
export const adminNoAuthRoutes = [
  { path: routes.adminLogin, element: <AdminLogin /> },
];

export const adminAuthRoutes = [
  { path: routes.adminDashboard, element: <AdminDashboard /> },
  { path: routes.adminTenants, element: <AdminTenants /> },
  { path: routes.adminTenantCreate, element: <AdminTenantForm /> },
  { path: routes.adminTenantEdit, element: <AdminTenantForm /> },
  { path: routes.adminTenantDetail, element: <AdminTenantDetail /> },
  { path: routes.adminBusinesses, element: <AdminBusinesses /> },
  { path: routes.adminReferenceData, element: <AdminReferenceData /> },
  { path: routes.adminBusinessDetail, element: <AdminBusinessDetail /> },
  { path: routes.adminCampaigns, element: <AdminCampaigns /> },
  { path: routes.adminCampaignDetail, element: <AdminCampaignDetail /> },
  { path: routes.adminAuditLogs, element: <AdminAuditLogs /> },
  { path: routes.adminPaymentsHealth, element: <AdminPaymentsHealth /> },
];

export const tenantStaffNoAuthRoutes = [
  { path: routes.tenantStaffLogin, element: <TenantStaffLogin /> },
];

export const tenantStaffAuthRoutes = [
  { path: routes.tenantStaffDashboard, element: <TenantStaffDashboard /> },
  { path: routes.tenantStaffCampaigns, element: <TenantStaffCampaigns /> },
  { path: routes.tenantStaffAdvertisers, element: <TenantStaffAdvertisers /> },
  {
    path: routes.tenantStaffBusinessDetail,
    element: <TenantStaffBusinessDetail />,
  },
  { path: routes.tenantStaffUsers, element: <TenantStaffUsers /> },
  { path: routes.tenantStaffStaff, element: <TenantStaffStaff /> },
  {
    path: routes.tenantStaffConsolidatedReport,
    element: <TenantStaffConsolidatedReport />,
  },
];
